/* eslint-disable no-param-reassign */
import _ from 'lodash';
import debug from 'debug';
import produce from 'immer';

import { CardFlowEvent, StoryStep } from 'types/story';
import { FLOW_DEFAULT_EVENT_NAME } from 'common/constants';
import { generateCardEventId, generateStepId } from 'common/utils/facades/utils';
import flowVars from 'admin/components/pages/Story/Flow/_vars.scss';

import { POINT_NAME } from './constants';

import listCss from '../List/List.scss';

const log = debug('app:flow:utils'); //eslint-disable-line

const MAIN_SCROLL_CONTAINER = '#root';

/**
 * Get coordinates of target from top left position of main scroll container
 * @param target
 * @return {{x1: Number, x2: number, y1: Number, y2: number, cy: number, cx: number}}
 */
const getTargetCoordinates = target => {
	const mainScrollContainer = document.querySelector(MAIN_SCROLL_CONTAINER);
	// @ts-expect-error ts-migrate FIXME
	const { scrollLeft, scrollTop } = mainScrollContainer; // default this is window.pageXOffset &&  window.pageYOffset

	const rect = target.getBoundingClientRect();
	const left = rect.left + scrollLeft;
	const top = rect.top + scrollTop - parseInt(flowVars.STORY_FLOW_OFFSET_TOP, 10);
	return {
		x1: left,
		x2: left + rect.width,
		y1: top,
		y2: top + rect.height,
		cy: top + rect.height / 2,
		cx: left + rect.width / 2,
		boundingClientRect: rect,
	};
};

/**
 * @param event {SyntheticEvent}
 * @param event.type {String}
 * @return {{MOUSEDOWN: boolean, MOUSEMOVE: boolean, MOUSEUP: boolean}}
 */
const checkEventType = ({ type }) => ({
	MOUSEDOWN: type === 'mousedown',
	MOUSEMOVE: type === 'mousemove',
	MOUSEUP: type === 'mouseup',
});

const CONNECTION_TYPES = {
	ANY: 'ANY', // bind to point by end point id
	NEXT: 'NEXT', // bind to 1st card in next step
} as const;

type CardEventProps = {
	_id?: CardFlowEvent['_id'];
	name?: CardFlowEvent['name'];
	componentId?: CardFlowEvent['componentId'];
	score?: CardFlowEvent['score'];
	pointId?: string;
};

/**
 * Create story card event model
 * @param props {Object}
 * @param props._id {String} unique event id
 * @param props.name {String} event name (expected to receive component type)
 * @param props.componentId {String} id of component the event related
 * @param props.pointId {String} id of card the event points to. (optional)
 * @param props.score {Array<Number, Number>} min & max story score values (optional)
 * @return {{_id: CardEvent.props._id, name: (CardEvent.props.name|string), connection: {type: *}}}
 * @constructor
 */
function CardEvent(props: CardEventProps = {}) {
	const event: CardFlowEvent = {
		_id: props._id || generateCardEventId(),
		name: props.name || FLOW_DEFAULT_EVENT_NAME,
		componentId: props.componentId,
	};

	if (props.score !== undefined) {
		event.score = props.score;
	}

	event.connection = { type: CONNECTION_TYPES.NEXT };

	if (props.pointId) {
		event.connection.type = CONNECTION_TYPES.ANY;
		event.connection.pointId = props.pointId;
	}

	return event;
}

/**
 *
 */
class Connection {
	constructor(props) {
		// @ts-expect-error ts-migrate FIXME
		this._id = props.id; // p1.pointId
		// @ts-expect-error ts-migrate FIXME
		this.type = props.type || CONNECTION_TYPES.ANY;

		this[POINT_NAME.p1] = {
			cx: _.get(props, [POINT_NAME.p1, 'cx']),
			cy: _.get(props, [POINT_NAME.p1, 'cy']),
			data: {
				stepId: null,
				cardId: null,
				pointId: null,
				node: null,
				// ... other data
			},
		};
		this[POINT_NAME.p2] = {
			cx: _.get(props, [POINT_NAME.p2, 'cx']),
			cy: _.get(props, [POINT_NAME.p2, 'cy']),
			data: {
				stepId: null,
				cardId: null,
				pointId: null,
				node: null,
				// ... other data
			},
		};

		// @ts-expect-error ts-migrate FIXME
		this.connected = this.setConnected(); // is p1 & p2 connected to some point?
	}

	setConnected(value = false) {
		const {
			data: { pointId: p1 },
		} = this[POINT_NAME.p1];
		const {
			data: { pointId: p2 },
		} = this[POINT_NAME.p2];
		// @ts-expect-error ts-migrate FIXME
		this.connected = p1 && p2 ? true : value;
	}

	setPoint(key, value) {
		this[key] = { ...this[key], ...value };
		this.setConnected();
	}

	updatePointCoordinates(pointName) {
		const pointToUpdate = this[pointName];
		const { cx, cy } = getTargetCoordinates(pointToUpdate.data.node);

		_.set(this, `${pointName}.cx`, cx);
		_.set(this, `${pointName}.cy`, cy);
	}
}

/**
 * @param points {Object}
 * @param stepId {String}
 * @return {Array}
 */
const getPointsByStep = ({ points, stepId }) => {
	return _.filter(points, point => point.stepId === stepId);
};

/**
 * @param connections {Object} object of Connection instances
 * @param stepId {String}
 * @return {Array}
 */
const getConnectionsByStepId = ({ connections, stepId }) => {
	const connectionsByStepId = [];

	_.forEach(connections, connection => {
		const { [POINT_NAME.p1]: P1, [POINT_NAME.p2]: P2 } = connection;

		if (_.get(P1, 'data.stepId') === stepId) {
			// @ts-expect-error ts-migrate FIXME
			connectionsByStepId.push({ connection, name: POINT_NAME.p1 });
		}
		if (_.get(P2, 'data.stepId') === stepId) {
			// @ts-expect-error ts-migrate FIXME
			connectionsByStepId.push({ connection, name: POINT_NAME.p2 });
		}
	});

	return connectionsByStepId;
};

/**
 * @param connection {Connection} A Connection instance
 * @param name {String}
 * @param destination {Object}
 */
const updateConnectionIn = ({ connection, name: pointName }, destination) => {
	// update connection
	connection.updatePointCoordinates(pointName);
	// update next connection with an updated connection
	_.set(destination, connection._id, connection);
};

/**
 * @param point {Object}
 * @param destination {Object}
 */
const updatePointIn = (point, destination) => {
	const updatedCoordinates = getTargetCoordinates(point.node);
	const destinationPoint = _.find(destination, _point => _point.pointId === point.pointId);
	_.assign(destinationPoint, updatedCoordinates);
};

/**
 * Check is "point" in visible "step" area
 * @param point {Object} A Connection instance point
 * @return {boolean}
 */
const isListPointInView = point => {
	const stepId = _.get(point, 'data.stepId');
	if (!stepId) {
		return true;
	}

	const list = document.getElementById(stepId);
	// @ts-expect-error ts-migrate FIXME
	const { top, bottom } = list.getBoundingClientRect();
	const pointTop = point.cy; // OR -> point.data.node.getBoundingClientRect().top;
	const listTop = top - parseInt(flowVars.STORY_FLOW_OFFSET_TOP, 10);

	return pointTop > listTop && pointTop < bottom;
};

/**
 * Calculating values to decide how should look a connection, according to "is his p1 || p2 in view"
 * @param connection {Connection} A Connection instance
 * @return {{ isStartPointInView: boolean, isEndPointInView: boolean}}
 */
const isConnectionInView = connection => {
	let isStartPointInView = true;
	let isEndPointInView = true;

	if (connection.connected) {
		isStartPointInView = isListPointInView(connection[POINT_NAME.p1]);
		isEndPointInView = isListPointInView(connection[POINT_NAME.p2]);
	}

	return {
		isStartPointInView,
		isEndPointInView,
	};
};

/**
 * @param connection {Connection} A Connection instance
 * @param isStartPointInView {Boolean}
 * @param isEndPointInView {Boolean}
 * @param curveStyle {Object} style object to **mutate**
 */
const updateConnectionOutOfView = ({ connection, isStartPointInView, isEndPointInView, curveStyle }) => {
	const showStart = isStartPointInView && !isEndPointInView;
	const showEnd = isEndPointInView && !isStartPointInView;

	const startStep = document.getElementById(connection[POINT_NAME.p1].data.stepId);
	const endStep = document.getElementById(connection[POINT_NAME.p2].data.stepId);
	const MAGIC = 0;

	if (showStart) {
		// log('updateConnectionOutOfView -> showStart');
		// @ts-expect-error ts-migrate FIXME
		const { offsetTop: top, offsetLeft: left } = endStep;
		// @ts-expect-error ts-migrate FIXME
		const bottom = top + endStep.offsetHeight;

		const isEndUp = connection[POINT_NAME.p1].cy > connection[POINT_NAME.p2].cy;
		if (isEndUp) {
			// case #1: show only START and end is UP
			// Y = step.top | X = p2.step.left
			connection[POINT_NAME.p2].cy = top - MAGIC;
			connection[POINT_NAME.p2].cx = left;
		} else {
			// case #2: show only START and end is DOWN
			// Y = step.bottom | X = p2.step.left
			connection[POINT_NAME.p2].cy = bottom + MAGIC;
			connection[POINT_NAME.p2].cx = left;
		}
		curveStyle.stroke = 'url("#fade-to-end")';
	}

	if (showEnd) {
		// log('updateConnectionOutOfView -> showEnd');
		// @ts-expect-error ts-migrate FIXME
		const { offsetTop: top, offsetLeft: left } = startStep;
		// @ts-expect-error ts-migrate FIXME
		const bottom = top + startStep.offsetHeight;
		// @ts-expect-error ts-migrate FIXME
		const right = left + startStep.offsetWidth;

		const isStartUp = connection[POINT_NAME.p1].cy < connection[POINT_NAME.p2].cy;
		if (isStartUp) {
			// case #3: show only END and start is UP
			// Y = step.top | X = p1.step.right
			connection[POINT_NAME.p1].cy = top - MAGIC;
			connection[POINT_NAME.p1].cx = right;
		} else {
			// case #4: show only END and start is DOWN
			// Y = step.bottom | X = p1.step.right
			connection[POINT_NAME.p1].cy = bottom - MAGIC;
			connection[POINT_NAME.p1].cx = right;
		}
		curveStyle.stroke = 'url("#fade-to-start")';
	}
};

const selectors = {
	STEP: `.${listCss.step}`, // draggable
	STEP_INNER: `.${listCss.stepInner}`, // scrollable
	POINT: `.${listCss.point}`,
	START_POINT: `.${listCss.pointStart}`,
	END_POINT: `.${listCss.pointEnd}`,
	CARD: `.${listCss.card}`,
};

/**
 * Get point nodes by selecter in some node
 * @param listNode {Element}
 * @param pointSelector {String}
 * @return {{}}
 */
function getPointsBySelector(listNode, pointSelector) {
	const result = {};
	const pointNodes = listNode.querySelectorAll(pointSelector);

	_.forEach(pointNodes, (node, i) => {
		result[i] = {
			...getTargetCoordinates(node),
			...node.dataset,
			node,
		};
	});

	return result;
}

/**
 * TODO: refactor to add to Connection
 * Create point in Connection
 * @param connection {Connection} Connection instance
 * @param pointId {String}
 * @param pointName {String} p1 || p2
 */
const createPoint = ({ connection, pointId, pointName }) => {
	const pointNode = document.querySelector(`.${listCss.point}[data-point-id="${pointId}"]`);

	if (!pointNode) {
		throw new Error(`Point [${pointId}] does not exists in DOM`);
	}

	const pointCoords = getTargetCoordinates(pointNode);
	connection.setPoint(pointName, {
		cx: pointCoords.cx,
		cy: pointCoords.cy,
		data: {
			// @ts-expect-error ts-migrate FIXME
			...pointNode.dataset,
			node: pointNode,
		},
	});
};

/**
 * @param steps {Array.<Object>}
 * @param cb {Function}
 */
const mapStepsEvents = (steps, cb) => {
	_.forEach(steps, (step, stepIndex) => {
		_.forEach(step.cards, (card, cardIndex) => {
			_.forEach(card.events, (event, eventIndex) => {
				cb({
					step,
					stepIndex,
					card,
					cardIndex,
					event,
					eventIndex,
				});
			});
		});
	});
};

/**
 * @param value {string} transform(Npx, Npx);
 * @return {*}
 */
const cssTransformLockY = value => {
	if (!value) {
		return value;
	}
	return value.split(',')[0].concat(', 0px)');
};

const createStep = (): StoryStep => {
	return {
		_id: generateStepId(),
		cards: [],
	};
};

const getDataWithAdditionalEmptyStep = data => {
	const lastStepIndex = _.size(data);
	const lastStep = data[lastStepIndex - 1];
	const lastStepsCardsLength = _.size(_.get(lastStep, 'cards'));
	if (lastStepsCardsLength || _.isEmpty(data)) {
		// create additional empty working step
		const nextData = produce(data, draft => {
			draft.push(createStep());
		});
		return nextData;
	}
	return data;
};

export {
	getTargetCoordinates,
	checkEventType,
	Connection,
	getConnectionsByStepId,
	updateConnectionIn,
	getPointsByStep,
	updatePointIn,
	isListPointInView,
	isConnectionInView,
	updateConnectionOutOfView,
	MAIN_SCROLL_CONTAINER,
	selectors,
	CONNECTION_TYPES,
	getPointsBySelector,
	createPoint,
	mapStepsEvents,
	cssTransformLockY,
	createStep,
	getDataWithAdditionalEmptyStep,
	CardEvent,
};
